import {
  Box,
  Grid,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
} from "@material-ui/core";
import React, { useEffect, useState, useRef } from "react";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import { useStyles } from "./style";
import { useFormik } from "formik";
import { useAppDispatch, useAppSelector } from "src/app-redux/hooks";
import CommonButton from "src/components/CommonButton/CommonButton";
import { object, string, number } from "yup";
import {
  amountDue,
  currentSelectedPaymentId,
  currentSelectedRegNo,
  setAmountDue,
} from "src/app-redux/commercial/commercialSlice";
import {
  addPaymentReceivedDrawer,
  getPaymentDetailsById,
} from "src/app-redux/commercial/actions/commercialActions";
import moment from "moment";
import { currencyUnitList, modesOfPayment } from "src/common/utils/app.dropdowns";
import { useDropzone } from "react-dropzone";
import { getAllFileCompressionConfigs } from "src/app-redux/configuration/actions/configurationActions";
import CommonTextFieldDate from "src/components/CommonTextFieldForDate/CommonTextFieldDate";
import CameraAltIcon from "@material-ui/icons/CameraAlt";
import Close from "@material-ui/icons/Close";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";

export interface IDrawerAddPaymentProps {}

export default function DrawerAddPayment(props: IDrawerAddPaymentProps) {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  //Redux
  const selectedPaymentId = useAppSelector(currentSelectedPaymentId);
  const selectedRegNo = useAppSelector(currentSelectedRegNo);
  const selectedDueAmount = useAppSelector(amountDue);
  const allCurrencies = currencyUnitList;
  const allModesOfPayment = modesOfPayment;
  const [fileToUpload, setFileToUpload] = React.useState<any>();
  const [validFileTypes, setValidFileTypes] = React.useState<any>([]);
  const [validFileSize, setValidFileSize] = React.useState(0);
  const [isValidFileType, setIsValidFileType] = React.useState<boolean>(true);
  const [isValidFileSize, setIsValidFileSize] = React.useState<boolean>(true);
  const [cameraOpen, setCameraOpen] = useState<boolean>(false);
  const [imageDataURL, setImageDataURL] = useState<any>(null);
  const [cameraNumber] = useState(0);
  const playerRef = useRef<HTMLVideoElement | null>(null);
  let fileTypesArr: any[] = [];
  let maxFileSize = 0;

  const onDrop = React.useCallback((acceptedFiles: File[]) => {
    const split = acceptedFiles[0].name.split(".");

    setIsValidFileType(fileTypesArr.includes(`.${split[split.length - 1]}`.toLowerCase()));
    setIsValidFileSize(acceptedFiles[0].size / (1024 * 1024) <= maxFileSize);

    setFileToUpload(acceptedFiles[0]);
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  useEffect(() => {
    getFileConfig();
  }, []);

  useEffect(() => {
    return () => {
      if (playerRef.current?.srcObject) {
        const tracks = (playerRef.current.srcObject as MediaStream).getTracks();
        tracks.forEach((track) => track.stop());
        playerRef.current.srcObject = null;
      }
    };
  }, []);

  const getFileConfig = async () => {
    const res = await dispatch(getAllFileCompressionConfigs());
    if (res.payload && res.payload[0]) {
      const fileTypes = res.payload[0]?.fileTypes?.split(",").map((type: string) => type.trim());
      setValidFileTypes(fileTypes);
      setValidFileSize(res.payload[0].fileSizeThreshold);
      fileTypesArr = fileTypes;
      maxFileSize = res.payload[0].fileSizeThreshold;
    }
  };

  // Camera capture functionality
  const dataURLtoFile = (dataurl: any, filename: any) => {
    const arr = dataurl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };

  const getListOfVideoInputs = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.filter((device) => device.kind === "videoinput");
    } catch (error) {
      console.error("Error enumerating devices:", error);
      return [];
    }
  };

  const openCamera = async () => {
    setImageDataURL(null);
    setCameraOpen(true);

    if (!("mediaDevices" in navigator)) {
      dispatch(
        addNotificationAction({
          message: "Camera not supported in this browser",
          options: { variant: "error" },
        })
      );
      return;
    }

    if (!("getUserMedia" in navigator.mediaDevices)) {
      dispatch(
        addNotificationAction({
          message: "Camera access not supported in this browser",
          options: { variant: "error" },
        })
      );
      return;
    }

    try {
      const videoInputs = await getListOfVideoInputs();

      if (videoInputs.length === 0) {
        dispatch(
          addNotificationAction({
            message: "The device does not have a camera",
            options: { variant: "error" },
          })
        );
        setCameraOpen(false);
        return;
      }
      if (cameraNumber >= videoInputs.length) {
        console.error("Invalid camera number");
        setCameraOpen(false);
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: videoInputs[cameraNumber].deviceId
            ? { ideal: videoInputs[cameraNumber].deviceId }
            : undefined,
        },
      });
      if (playerRef.current) {
        playerRef.current.srcObject = stream;
      } else {
        console.error("Video player element not found");
        setCameraOpen(false);
      }
    } catch (error) {
      if (error instanceof Error && error.name === "OverconstrainedError") {
        dispatch(
          addNotificationAction({
            message: "The selected camera is not available. Falling back to the default camera.",
            options: { variant: "warning" },
          })
        );
        try {
          const fallbackStream = await navigator.mediaDevices.getUserMedia({ video: true });
          if (playerRef.current) {
            playerRef.current.srcObject = fallbackStream;
          }
        } catch (fallbackError) {
          dispatch(
            addNotificationAction({
              message: "An error occurred while accessing the camera.",
              options: { variant: "error" },
            })
          );
          setCameraOpen(false);
        }
      } else {
        dispatch(
          addNotificationAction({
            message: "An error occurred while accessing the camera.",
            options: { variant: "error" },
          })
        );
        setCameraOpen(false);
      }
    }
  };

  const closeCamera = () => {
    setImageDataURL(null);
    if (playerRef.current?.srcObject) {
      const tracks = (playerRef.current.srcObject as MediaStream).getTracks();
      tracks.forEach((track) => track.stop());
      playerRef.current.srcObject = null;
    }
    setCameraOpen(false);
  };

  const captureImage = async () => {
    if (!playerRef.current) return;

    const canvas = document.createElement("canvas");
    canvas.width = playerRef.current.videoWidth;
    canvas.height = playerRef.current.videoHeight;
    const context = canvas.getContext("2d");
    if (context) {
      context.drawImage(playerRef.current, 0, 0, canvas.width, canvas.height);
      const imageSrc = canvas.toDataURL();

      // Stop the camera stream
      if (playerRef.current?.srcObject) {
        const tracks = (playerRef.current.srcObject as MediaStream).getTracks();
        tracks.forEach((track) => track.stop());
        playerRef.current.srcObject = null;
      }

      setImageDataURL(imageSrc);
    }
  };

  const confirmAndAddImage = async () => {
    if (!imageDataURL) return;

    // Use validFileTypes state and normalize comparison (trim and lowercase)
    const validTypes = validFileTypes.length > 0 ? validFileTypes : fileTypesArr;
    const normalizedValidTypes = validTypes.map((type: string) => type.trim().toLowerCase());

    // Determine which image extension to use (.jpg or .jpeg) based on what's in allowed types
    let imageExtension = ".jpg";
    if (normalizedValidTypes.includes(".jpeg") && !normalizedValidTypes.includes(".jpg")) {
      imageExtension = ".jpeg";
    } else if (normalizedValidTypes.includes(".jpg")) {
      imageExtension = ".jpg";
    } else if (normalizedValidTypes.includes(".jpeg")) {
      imageExtension = ".jpeg";
    }

    // Convert to file and set as fileToUpload
    const timestamp = moment().format("YYYYMMDD_HHmmss");
    const capturedFile = dataURLtoFile(imageDataURL, `CameraImage_${timestamp}${imageExtension}`);

    // Validate file type and size
    const split = capturedFile.name.split(".");
    const fileExtension = `.${split[split.length - 1]}`.toLowerCase();
    const isValidType = normalizedValidTypes.includes(fileExtension);
    const isValidSize = capturedFile.size / (1024 * 1024) <= validFileSize;

    if (!isValidType) {
      dispatch(
        addNotificationAction({
          message: `Only ${validTypes.join(", ")} files are allowed.`,
          options: { variant: "error" },
        })
      );
      setImageDataURL(null);
      openCamera();
      return;
    }

    if (!isValidSize) {
      dispatch(
        addNotificationAction({
          message: `File size cannot exceed ${validFileSize}MB.`,
          options: { variant: "error" },
        })
      );
      setImageDataURL(null);
      openCamera();
      return;
    }

    // Set as fileToUpload
    setFileToUpload(capturedFile);
    setIsValidFileType(isValidType);
    setIsValidFileSize(isValidSize);

    // Close camera and show success message
    closeCamera();
    dispatch(
      addNotificationAction({
        message: "Image captured and added successfully",
        options: { variant: "success" },
      })
    );
  };

  const removeFile = () => {
    setFileToUpload(null);
    setIsValidFileType(true);
    setIsValidFileSize(true);
  };

  const validationSchema = object({
    amount: number()
      .required("This field is required")
      .min(1, "Minimum 1 is allowed.")
      .max(
        selectedDueAmount,
        selectedDueAmount > 0
          ? `Maximum ${selectedDueAmount} is allowed.`
          : "Payment already received"
      ),
    currency: string().required("This field is required"),
    paymentMethod: string().required("This field is required"),
    remarks: string().max(3000, "Maximum 3000 characters allowed"),
  });

  const formik = useFormik<any>({
    enableReinitialize: true,
    initialValues: {
      gatePassPaymentId: selectedPaymentId,
      registerationNumber: selectedRegNo,
      paymentDate: moment(new Date()).format("YYYY-MM-DDTHH:mm"),
      amount: "",
      currency: "PKR",
      paymentMethod: "Cash",
      remarks: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values: any, actions) => {
      const res: any = await dispatch(
        addPaymentReceivedDrawer({ ...values, fileRecord: fileToUpload })
      );
      dispatch(getPaymentDetailsById(selectedPaymentId));
      if (res.payload) {
        dispatch(setAmountDue(selectedDueAmount - formik.values.amount));
        actions.setSubmitting(false);
        formik.resetForm();
        formik.setValues(formik.initialValues);
        setFileToUpload(null);
        setIsValidFileType(true);
        setIsValidFileSize(true);
        return;
      }
    },
  });
  return (
    <div className={classes.drawerDetails}>
      {/* Drawer Status Item  */}

      <Grid container spacing={2} className="topContainer" style={{ marginTop: "5px" }}>
        <Grid container direction="row" className="itemRow" spacing={2}>
          {/* Payment Date */}
          <Grid item xs={12} md={12} style={{ height: "55px" }}>
            <CommonTextFieldDate
              cssClassName="customTextField"
              label={"Date"}
              name={"paymentDate"}
              value={formik.values.paymentDate}
              required={true}
              onChange={(e: any) => {
                formik.setFieldValue("paymentDate", e);
              }}
              maxDate={moment().format("YYYY-MM-DDTHH:mm")}
              error={formik.touched.paymentDate && Boolean(formik.errors.paymentDate)}
              helperText={formik.touched.paymentDate && formik.errors.paymentDate}
            />
            {/* <Box>
              <TextField
                type="datetime-local"
                required
                size="small"
                fullWidth
                variant="outlined"
                name="paymentDate"
                label="Date"
                defaultValue={formik.values.paymentDate}
                InputProps={{
                  inputProps: { max: moment(new Date()).format("YYYY-MM-DDTHH:mm") || "" },
                }}
                onChange={(event: any) => {
                  formik.setFieldValue("paymentDate", event.target.value);
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                disabled={false}
                error={formik.touched.paymentDate && Boolean(formik.errors.paymentDate)}
                helperText={formik.touched.paymentDate && formik.errors.paymentDate}
              />
            </Box> */}
          </Grid>
          {/* Amount */}
          <Grid item xs={12} md={12}>
            <Box>
              <TextField
                className="customTextField"
                type="number"
                required
                size="small"
                fullWidth
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  inputProps: { min: 0, max: selectedDueAmount },
                }}
                label="Amount"
                name="amount"
                placeholder="Enter Amount"
                disabled={false}
                value={formik.values.amount}
                onChange={formik.handleChange}
                error={formik.touched.amount && Boolean(formik.errors.amount)}
                helperText={formik.touched.amount && formik.errors.amount}
              />
            </Box>
            <Grid container direction="row" justifyContent="flex-end" style={{ marginTop: "5px" }}>
              <Box fontWeight={700}>Amount Pending:</Box>
              <Box marginLeft={"5px"}>
                PKR {selectedDueAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </Box>
            </Grid>
          </Grid>
          {/* Currency */}
          <Grid item xs={12} md={12}>
            <Box>
              <CommonAutoCompleteField
                cssClassName="customTextField"
                disabled={false}
                label="Currency"
                name="currency"
                optionsType={allCurrencies}
                getOptionLabel={(option) => option}
                required
                value={allCurrencies.find((st: any) => st === formik.values.currency) || null}
                onChange={(option: any) => {
                  option && option !== "" ? formik.setFieldValue("currency", option) : "";
                }}
                error={formik.touched.currency && Boolean(formik.errors.currency)}
                helperText={formik.touched.currency && formik.errors.currency}
              />
            </Box>
          </Grid>
          {/* Mode of Payment */}
          <Grid item xs={12} md={12}>
            <Box>
              <CommonAutoCompleteField
                cssClassName="customTextField"
                label="Mode of Payment"
                placeholder="Mode 0f Payment"
                name="paymentMethod"
                optionsType={allModesOfPayment}
                required
                getOptionLabel={(option: any) => option || ""}
                value={
                  allModesOfPayment.find((st: any) => st === formik.values.paymentMethod) || null
                }
                onChange={(option: any) => {
                  option && option !== "" ? formik.setFieldValue("paymentMethod", option) : "";
                }}
                error={formik.touched.paymentMethod && Boolean(formik.errors.paymentMethod)}
                helperText={formik.touched.paymentMethod && formik.errors.paymentMethod}
              />
            </Box>
          </Grid>
          {/* Remarks */}
          <Grid item xs={12} md={12}>
            <Box>
              <TextField
                className="customTextField"
                type="string"
                size="small"
                multiline
                rows={1}
                rowsMax={8}
                fullWidth
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                label="Remarks"
                name="remarks"
                placeholder="Enter Your Remarks"
                disabled={false}
                value={formik.values.remarks}
                onChange={formik.handleChange}
                error={formik.touched.remarks && Boolean(formik.errors.remarks)}
                helperText={formik.touched.remarks && formik.errors.remarks}
              />
            </Box>
          </Grid>
          {/* File Upload */}
          <Grid item xs={12} md={12}>
            <Grid container item xs={12} md={12}>
              <Grid item xs={12} md={12}>
                <Box textAlign="left" mt={1} mb={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={openCamera}
                    startIcon={<CameraAltIcon />}
                    style={{ backgroundColor: "#E67817", color: "#fff", marginRight: "8px" }}
                  >
                    Capture Image
                  </Button>
                </Box>
              </Grid>
            </Grid>
            <Box
              {...getRootProps()}
              p={0}
              pl={1}
              border={"1px solid #72CA00"}
              borderRadius={5}
              height={fileToUpload ? "auto" : 38}
              pb={4}
              overflow={"auto"}
              display={fileToUpload ? "flex" : "block"}
              flexWrap={"wrap"}
              borderColor={isDragActive ? "error.main" : "secondary.main"}
              style={{ cursor: "pointer" }}
            >
              <input {...getInputProps()} />

              {fileToUpload ? (
                <div
                  style={{
                    marginTop: "6px",
                    backgroundColor: "#E67817",
                    padding: "3px 15px 3px 5px",
                    borderRadius: "10px",
                    maxWidth: "122px",
                    display: "flex",
                    flexWrap: "wrap",
                    marginLeft: "5px",
                  }}
                >
                  <div
                    style={{
                      maxWidth: "100px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontSize: "11px",
                      color: "white",
                    }}
                  >
                    {fileToUpload.name}
                  </div>
                  <IconButton
                    aria-label="Remove file"
                    onClick={(event) => {
                      event.stopPropagation();
                      removeFile();
                    }}
                    edge="end"
                    style={{
                      color: "white",
                      padding: 0,
                      marginTop: "2px",
                      width: "16px",
                      height: "16px",
                      overflow: "hidden",
                      marginLeft: "4px",
                    }}
                    size="small"
                  >
                    <Close style={{ fontSize: "14px" }} />
                  </IconButton>
                </div>
              ) : isDragActive ? (
                <p>Drop a file here ...</p>
              ) : (
                <p style={{ color: "rgb(181 178 178)", marginTop: 9, paddingBottom: 10 }}>
                  {/* Drag and drop file here,or click to select a file */}
                  Upload File
                </p>
              )}
            </Box>
            <Box>
              <p style={{ color: "#f44336", marginBottom: 0 }}>
                <ul
                  style={{
                    paddingLeft: "16px",
                    margin: 0,
                    display: isValidFileType ? "none" : "block",
                  }}
                >
                  <li>Only {validFileTypes} files are allowed.</li>
                </ul>
                <ul
                  style={{
                    paddingLeft: "16px",
                    margin: 0,
                    display: isValidFileSize ? "none" : "block",
                  }}
                >
                  <li>File size cannot exceed {validFileSize}MB.</li>
                </ul>
              </p>
            </Box>
          </Grid>

          <Grid container item xs={12} md={12}>
            <Grid item xs={12} md={9}>
              <Box textAlign="left" mt={1}>
                <CommonButton
                  cssClass="customSubmitBtn"
                  onClick={() => {
                    formik.resetForm();
                    formik.setValues(formik.initialValues);
                    setFileToUpload(null);
                    setIsValidFileType(true);
                    setIsValidFileSize(true);
                  }}
                >
                  Reset
                </CommonButton>
                <CommonButton
                  cssClass="customSubmitBtn"
                  isGreen
                  disable={!isValidFileType || !isValidFileSize}
                  onClick={() => {
                    formik.submitForm();
                  }}
                >
                  Save
                </CommonButton>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Camera Dialog */}
      <Dialog
        open={cameraOpen}
        onClose={closeCamera}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: "#000",
          },
        }}
      >
        <DialogTitle style={{ backgroundColor: "#000", color: "#fff" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Capture Image</Typography>
            <IconButton onClick={closeCamera} style={{ color: "#fff" }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px",
              backgroundColor: "#000",
            }}
          >
            {imageDataURL ? (
              <img
                src={imageDataURL}
                alt="captured"
                style={{ maxWidth: "100%", maxHeight: "500px" }}
              />
            ) : (
              <video
                ref={playerRef}
                autoPlay
                playsInline
                style={{
                  maxWidth: "100%",
                  maxHeight: "500px",
                  backgroundColor: "#000",
                }}
              >
                <track kind="captions" />
              </video>
            )}
          </Box>
        </DialogContent>
        <DialogActions style={{ backgroundColor: "#000", padding: "16px" }}>
          {!imageDataURL && (
            <>
              <Button
                onClick={closeCamera}
                style={{ color: "#fff", marginRight: "8px" }}
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                onClick={captureImage}
                style={{ backgroundColor: "#E67817", color: "#fff" }}
                variant="contained"
              >
                Capture
              </Button>
            </>
          )}
          {imageDataURL && (
            <>
              <Button
                onClick={() => {
                  setImageDataURL(null);
                  openCamera();
                }}
                style={{ color: "#fff", marginRight: "8px" }}
                variant="outlined"
              >
                Retake
              </Button>
              <Button
                onClick={confirmAndAddImage}
                style={{ backgroundColor: "#E67817", color: "#fff" }}
                variant="contained"
              >
                Add Image
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
}
